import { useState, useEffect } from "react";
import { Download, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISSED_KEY = "nutriflow-pwa-install-dismissed";

export function PwaInstallPrompt() {
  const { t } = useTranslation();
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY)) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  if (!visible || !installEvent) return null;

  const handleInstall = async () => {
    await installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label={t('pwa.installTitle')}
      className="fixed bottom-20 lg:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50
                 bg-white/95 backdrop-blur-lg dark:bg-zinc-900/95 border border-zinc-200 dark:border-zinc-700
                 rounded-2xl shadow-xl p-4 flex items-start gap-3
                 animate-[portal-fade-in_0.3s_ease-out]"
    >
      <img
        src="/icarus_logo_600px.webp"
        alt=""
        aria-hidden="true"
        className="w-10 h-10 rounded-lg flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-zinc-900 dark:text-zinc-100">
          {t('pwa.installTitle')}
        </p>
        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
          {t('pwa.installBody')}
        </p>
        <Button
          size="sm"
          className="mt-2 h-8 text-xs bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
          onClick={handleInstall}
        >
          <Download aria-hidden="true" className="w-3.5 h-3.5 mr-1.5" />
          {t('pwa.installCta')}
        </Button>
      </div>
      <button
        onClick={handleDismiss}
        className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 flex-shrink-0 mt-0.5 p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label={t('pwa.dismiss')}
      >
        <X aria-hidden="true" className="w-4 h-4" />
      </button>
    </div>
  );
}
